// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { Box, Divider, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { CollateralAgreement_SettlementObligation } from '@daml.js/utility-collateral-app-v2/lib/Utility/Collateral/App/Model/Collateral';
import { ContractId } from '@daml/types';
import { MoreActionsMenu } from '../../../components/MoreActionsMenu';
import PartyDisplay from '../../../components/PartyDisplay';
import { StateCell } from '../../../components/StateCell';
import { DataTable } from '../../../components/Table/DataTable';
import { useModuleParty } from '../../../contexts/ModuleContext';
import { PageWrapper } from '../../PageWrapper';
import { GenericAction } from '../types';
import { CancelInstructDialog } from './CancelInstructDialog';
import { DisputeSettlementObligationDialog } from './DisputeSettlementObligationDialog/DisputeSettlementObligationDialog';
import { FulfilSettlementObligationDialog } from './FulfilSettlementObligationDialog/FulfilSettlementObligationDialog';
import { InstructDetailsDialog } from './InstructDetailsDialog';
import { InstructedPopover } from './InstructedPopover';
import { Positions } from './Positions';
import { SecuredPartyPopover } from './SecuredPartyPopover';
import { SettledDetailsDialog } from './SettledDetails';
import { InstructedRow } from './types';
import { useGetCollateralRows } from './useGetCollateralRows';
import { useObligationsActions } from './useObligationsActions';

interface DisplayedDateProps {
  date: Date;
}

const DisplayedDate: React.FC<DisplayedDateProps> = ({ date }) => {
  return (
    <Tooltip
      arrow
      placement="top"
      title={<Typography variant="body2">{date.toLocaleString()}</Typography>}
    >
      <Typography variant="body2">{date.toLocaleDateString()}</Typography>
    </Tooltip>
  );
};

export const Obligations: React.FC = () => {
  const { rows } = useGetCollateralRows();

  const [selectedCid, setSelectedCid] = React.useState('');
  const setSelectedRow = (r: InstructedRow) => {
    setSelectedCid(r.contractId);
  };

  const { createCollateralsActions, onSettle, onClose, dialogName, onCancel } =
    useObligationsActions();
  const selected = rows.find((r) => r.contractId === selectedCid);
  const [tabIndex, setTabIndex] = React.useState(0);
  const party = useModuleParty();
  const displayedRows = rows.filter((r) => {
    if (tabIndex === 0) {
      return true;
    }
    if (tabIndex === 1) {
      return r.state === 'Pending';
    }
    if (tabIndex === 2) {
      return r.state === 'Instructed' || r.state === 'Allocated';
    }
    if (tabIndex === 3) {
      return r.state === 'Settled';
    }
    return true;
  });
  const onChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };
  const createHeaders: (
    createActions: (r: InstructedRow) => GenericAction<InstructedRow>[],
  ) => GridColDef<InstructedRow>[] = (
    createActions: (r: InstructedRow) => GenericAction<InstructedRow>[],
  ) => {
    const renderActionCell = (params: GridRenderCellParams<InstructedRow>) => {
      const actions = createActions(params.row);

      if (actions.length === 0) {
        return null;
      }

      return (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <MoreActionsMenu actions={actions} row={params.row} setSelectedRow={setSelectedRow} />
        </Box>
      );
    };

    return [
      {
        field: 'state',
        headerName: 'State',
        renderCell: (params) => {
          const isPledgor =
            params.row.payload.positions.filter((p) => p.pledgor === party).length > 0;
          const popover = isPledgor ? <InstructedPopover /> : <SecuredPartyPopover />;
          return (
            <StateCell
              popover={params.row.state === 'Instructed' && popover}
              state={params.row.state}
            />
          );
        },
        flex: 1,
        filterable: true,
        valueGetter: (params) => params.row.state,
      },
      {
        field: 'date',
        headerName: 'Created at',
        valueGetter: (params) => params.row.payload.createdAt,
        renderCell: (params) => <DisplayedDate date={new Date(params.row.payload.createdAt)} />,
        flex: 1,
        filterable: true,
      },
      {
        field: 'id',
        headerName: 'Agreement Id',
        valueGetter: (params) => params.row.payload.agreementId,
        flex: 1,
        filterable: true,
      },
      {
        field: 'Sender',
        headerName: 'Party A',
        valueGetter: (params) => params.row.payload.partyA,
        renderCell: (params) => <PartyDisplay party={params.row.payload.partyA} />,
        flex: 1,
        filterable: true,
      },
      {
        field: 'PartyB',
        headerName: 'Party B',
        renderCell: (params) => <PartyDisplay party={params.row.payload.partyB} />,
        flex: 1,
        filterable: true,
      },
      {
        field: 'positions',
        headerName: 'positions',
        renderCell: (params) => <Positions positions={params.row.payload.positions} />,
        flex: 1,
        filterable: false,
      },
      {
        field: 'action',
        headerName: '',
        align: 'right',
        sortable: false,
        renderCell: (params) => renderActionCell(params),
        flex: 0.5,
        filterable: false,
      },
    ];
  };
  const tabs = ['All', 'Pending', 'Instructed', 'Settled'];
  return (
    <PageWrapper pageName="Obligations">
      <Tabs value={tabIndex} onChange={onChange}>
        {tabs.map((t, i) => (
          <Tab key={t} sx={{ textTransform: 'capitalize' }} value={i} label={t} />
        ))}
      </Tabs>
      <Divider />

      <DataTable
        columns={createHeaders(createCollateralsActions)}
        rows={displayedRows}
        rowKey={(a) => a.contractId}
      />
      {dialogName === 'fulfilSettlementObligation' && selected && selected.payload.agreementCId && (
        // We know that it is SettlementObligation CId because only pending rows can open this dialog
        <FulfilSettlementObligationDialog
          settlementObligationCId={
            selected.contractId as ContractId<CollateralAgreement_SettlementObligation>
          }
          collateralAgreementCId={selected.payload.agreementCId}
          open
          onClose={onClose}
        />
      )}
      {dialogName === 'disputeSettlementObligation' && selected && (
        // We know that it is SettlementObligation CId because only pending rows can open this dialog
        <DisputeSettlementObligationDialog
          settlementObligationCId={
            selected.contractId as ContractId<CollateralAgreement_SettlementObligation>
          }
          open
          onClose={onClose}
        />
      )}
      {dialogName === 'cancelInstruct' && (
        <CancelInstructDialog row={selected} open onCancel={onCancel} onClose={onClose} />
      )}
      {dialogName === 'instruct' && (
        <InstructDetailsDialog open row={selected} onSubmit={onSettle} onClose={onClose} />
      )}
      {dialogName === 'details' && <SettledDetailsDialog open row={selected} onClose={onClose} />}
    </PageWrapper>
  );
};
